import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus, Rocket, Clock, X as XIcon, ArrowClockwise, Envelope,
  CheckCircle, Warning, Trash, Copy, PaperPlaneTilt, Megaphone,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

function daysFromNow(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.max(0, Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24)));
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminPilotsPage() {
  const { user } = useAuth();
  const [pilots, setPilots] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", days: 90, note: "" });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/pilots");
      setPilots(data.pilots || []);
      setInvites(data.invites || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pilots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSubmitting(true);
    try {
      const { data } = await client.post("/admin/pilots/grant", {
        email: form.email.trim(),
        days: Number(form.days) || 90,
        note: form.note || null,
      });
      if (data.status === "extended") {
        toast.success(`✅ Extended access for ${data.email} — ${data.days} days`);
      } else {
        toast.success(`📧 Invite created for ${data.email}. They'll auto-get ${data.days}-day trial when they sign up.`);
      }
      setForm({ email: "", days: form.days, note: "" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed");
    } finally {
      setSubmitting(false);
    }
  };

  const extend = async (userId, days = 90) => {
    try {
      await client.post(`/admin/pilots/${userId}/extend`, { email: "noop@noop.com", days });
      toast.success(`Extended by ${days} more days`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    }
  };

  const revoke = async (userId, email) => {
    if (!window.confirm(`Revoke pilot access for ${email}? Their trial will end immediately.`)) return;
    try {
      await client.post(`/admin/pilots/${userId}/revoke`);
      toast.success("Pilot access revoked");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const resendWelcome = async (userId, email) => {
    if (!window.confirm(`Re-send the founder welcome email to ${email}?\n\nUse this when a signup email got caught in spam OR to gently re-engage a cold trial.`)) return;
    try {
      await client.post(`/admin/users/${userId}/resend-welcome`);
      toast.success(`Welcome email re-sent to ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const askTestimonial = async (userId, email) => {
    if (!window.confirm(`Send a personalized AI-drafted email to ${email} asking for a testimonial?\n\nThey'll get a link to a form where they can submit 2-3 sentences. Their reply lands as a draft in /app/admin/testimonials for you to review + publish.`)) return;
    try {
      const { data } = await client.post(`/admin/testimonial-requests/${userId}`);
      const label = data.reused_existing_request
        ? `Re-sent the same ask link to ${email}`
        : `Testimonial ask sent to ${email}`;
      toast.success(label);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ask email failed");
    }
  };

  const deleteInvite = async (email) => {
    if (!window.confirm(`Delete pending invite for ${email}?`)) return;
    try {
      await client.delete(`/admin/pilots/invite/${encodeURIComponent(email)}`);
      toast.success("Invite deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const copySignupLink = async (email) => {
    const link = `${window.location.origin}/signup?email=${encodeURIComponent(email)}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Signup link copied — send it to your friend");
    } catch {
      toast.error("Couldn't copy — link: " + link);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="card-surface p-8 text-center" data-testid="admin-pilots-forbidden">
        <Warning size={40} weight="fill" className="text-red-400 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-white mb-2">Admin only</h1>
        <p className="text-neutral-400 text-sm">This tool is restricted to admin accounts.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pilots-page">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
          <Rocket size={12} weight="fill" /> FOUNDER TOOL
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">Pilot Access</h1>
        <p className="text-neutral-400 mt-2 text-sm sm:text-base">
          Grant free extended trials to pilot users, testers, and early customers. They skip Stripe entirely for the duration.
        </p>
      </div>

      {/* Grant form */}
      <div className="card-surface p-6" data-testid="pilot-grant-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-4">
          <UserPlus size={14} /> Grant Pilot Access
        </div>
        <form onSubmit={grant} className="grid grid-cols-1 sm:grid-cols-12 gap-3">
          <div className="sm:col-span-5">
            <label className="block text-xs text-neutral-400 mb-1">Friend's email</label>
            <div className="relative">
              <Envelope size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="friend@trucking.com"
                data-testid="pilot-grant-email"
                className="w-full rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm py-2.5 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-neutral-400 mb-1">Days</label>
            <input
              type="number"
              min={1}
              max={365}
              required
              value={form.days}
              onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))}
              data-testid="pilot-grant-days"
              className="w-full rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs text-neutral-400 mb-1">Note (optional)</label>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="e.g. Trucking buddy Jake"
              data-testid="pilot-grant-note"
              className="w-full rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div className="sm:col-span-2 flex items-end">
            <button
              type="submit"
              disabled={submitting || !form.email}
              data-testid="pilot-grant-submit"
              className="w-full rounded-full px-4 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <Rocket size={13} weight="bold" /> {submitting ? "…" : "Grant"}
            </button>
          </div>
        </form>
        <div className="mt-4 flex flex-wrap gap-2">
          {[30, 60, 90, 180].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setForm((f) => ({ ...f, days: d }))}
              data-testid={`pilot-preset-${d}`}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                Number(form.days) === d
                  ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/15 text-[#FF5A1F]"
                  : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-4 leading-relaxed">
          <b className="text-neutral-300">How it works:</b> If they've already signed up, this extends their trial.
          If they haven't yet, we create a pending invite — when they sign up with that email, they auto-get the extended trial (no Stripe required).
        </p>
      </div>

      {/* Active pilots */}
      <div className="card-surface p-6" data-testid="pilot-active-list">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
            <CheckCircle size={14} weight="fill" className="text-emerald-400" /> Active Pilots ({pilots.length})
          </div>
          <button
            onClick={reload}
            data-testid="pilot-reload-btn"
            className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1"
          >
            <ArrowClockwise size={12} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="text-neutral-500 text-sm py-6 text-center">Loading…</div>
        ) : pilots.length === 0 ? (
          <div className="text-neutral-500 text-sm py-6 text-center border border-dashed border-white/10 rounded-lg">
            No active pilots yet. Grant your first one above ↑
          </div>
        ) : (
          <div className="space-y-2">
            {pilots.map((p) => {
              const rem = p.days_remaining ?? daysFromNow(p.trial_ends_at);
              const expired = rem === 0;
              return (
                <div
                  key={p.id}
                  data-testid={`pilot-row-${p.email}`}
                  className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border ${
                    expired ? "border-red-500/30 bg-red-500/[0.03]" : "border-white/10 bg-white/[0.02]"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium text-sm truncate">{p.email}</div>
                    <div className="text-xs text-neutral-500 truncate">
                      {p.full_name || "—"} · {p.company_name || "—"}
                      {p.pilot_note ? <> · <span className="text-neutral-400">{p.pilot_note}</span></> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${expired ? "text-red-400" : rem <= 7 ? "text-amber-400" : "text-emerald-400"}`}>
                        {expired ? "Expired" : `${rem}d left`}
                      </div>
                      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">
                        until {fmtDate(p.trial_ends_at)}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => askTestimonial(p.id, p.email)}
                        title="Ask for a testimonial (AI-drafted email)"
                        data-testid={`pilot-ask-testimonial-${p.email}`}
                        className="px-2 py-1 rounded text-xs border border-white/10 text-neutral-300 hover:text-[#FF9764] hover:border-[#FF5A1F]/40"
                      >
                        <Megaphone size={11} weight="fill" />
                      </button>
                      <button
                        onClick={() => resendWelcome(p.id, p.email)}
                        title="Re-send the founder welcome email"
                        data-testid={`pilot-resend-welcome-${p.email}`}
                        className="px-2 py-1 rounded text-xs border border-white/10 text-neutral-300 hover:text-[#FF9764] hover:border-[#FF5A1F]/40"
                      >
                        <PaperPlaneTilt size={11} weight="fill" />
                      </button>
                      <button
                        onClick={() => extend(p.id, 30)}
                        title="Extend +30 days"
                        data-testid={`pilot-extend-${p.email}`}
                        className="px-2 py-1 rounded text-xs border border-white/10 text-neutral-300 hover:text-white hover:border-white/30"
                      >
                        +30d
                      </button>
                      <button
                        onClick={() => extend(p.id, 90)}
                        title="Extend +90 days"
                        className="px-2 py-1 rounded text-xs border border-white/10 text-neutral-300 hover:text-white hover:border-white/30"
                      >
                        +90d
                      </button>
                      <button
                        onClick={() => revoke(p.id, p.email)}
                        title="Revoke access"
                        data-testid={`pilot-revoke-${p.email}`}
                        className="px-2 py-1 rounded text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <XIcon size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pending invites */}
      <div className="card-surface p-6" data-testid="pilot-invite-list">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-4">
          <Clock size={14} /> Pending Invites ({invites.length})
        </div>
        {loading ? (
          <div className="text-neutral-500 text-sm py-6 text-center">Loading…</div>
        ) : invites.length === 0 ? (
          <div className="text-neutral-500 text-sm py-6 text-center border border-dashed border-white/10 rounded-lg">
            No pending invites. Emails granted before their signup will appear here.
          </div>
        ) : (
          <div className="space-y-2">
            {invites.map((inv) => (
              <div
                key={inv.email}
                data-testid={`pilot-invite-${inv.email}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium text-sm truncate">{inv.email}</div>
                  <div className="text-xs text-neutral-500 truncate">
                    {inv.days} days trial · granted {fmtDate(inv.granted_at)}
                    {inv.note ? <> · {inv.note}</> : null}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => copySignupLink(inv.email)}
                    title="Copy signup link"
                    data-testid={`pilot-invite-copy-${inv.email}`}
                    className="px-2 py-1 rounded text-xs border border-white/10 text-neutral-300 hover:text-white hover:border-white/30 inline-flex items-center gap-1"
                  >
                    <Copy size={11} /> Link
                  </button>
                  <button
                    onClick={() => deleteInvite(inv.email)}
                    title="Delete invite"
                    data-testid={`pilot-invite-delete-${inv.email}`}
                    className="px-2 py-1 rounded text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
